import PropTypes from "prop-types"
import React, { useState } from "react"
import { useForm } from "react-hook-form"

import Button from "src/components/Button"
import Form from "src/components/Form"
import InlineNotice from "src/components/InlineNotice"
import Modal from "src/components/Modal"

import { useTracker } from "src/hooks/use_tracker"

const VERSIONS = {
  customer: {
    label: "Customer Copy",
    description:
      "Boater-facing document with scope, jobs, pricing, and billing status.",
  },
}

const PrintWorkOrderModal = ({ isOpen, onClose, workOrder, onPrint }) => {
  const { register, handleSubmit, watch, reset } = useForm({
    defaultValues: { version: "" },
  })
  const version = watch("version")
  const tracker = useTracker()
  const [pendingVersion, setPendingVersion] = useState(null)

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleDiscard = () => {
    tracker.trackEvent("work_orders:print_work_order:discarded")
    handleClose()
  }

  const onSubmit = ({ version: selected }) => {
    tracker.trackEvent("work_orders:print_work_order:confirmed", {
      version: selected,
    })
    // Defer the print until the modal has fully closed (afterLeave). window.print()
    // blocks the main thread; firing it during the modal's close transition
    // strands base-ui's backdrop portal, leaving an invisible full-screen
    // click-blocker until the page is refreshed.
    setPendingVersion(selected)
    handleClose()
  }

  const handleAfterLeave = () => {
    if (!pendingVersion) return
    onPrint(pendingVersion)
    setPendingVersion(null)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDiscard}
      afterLeave={handleAfterLeave}
      size="default"
    >
      <Modal.Header>
        <h4 className="m-0 text-xl font-bold">Print work order</h4>
        <div className="mt-1 text-sm text-gray-500">
          {workOrder.displaySequentialId} · choose a version to print or
          download.
        </div>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Form.Label htmlFor="print-version">Print version</Form.Label>
          <Form.Select id="print-version" {...register("version")}>
            <option value="">Choose a version…</option>
            {Object.entries(VERSIONS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
          {version && (
            <div className="mt-3">
              <InlineNotice>{VERSIONS[version].description}</InlineNotice>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button variant="tertiary" onClick={handleDiscard}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!version}>
              Print
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

PrintWorkOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  workOrder: PropTypes.shape({
    displaySequentialId: PropTypes.string.isRequired,
  }).isRequired,
}

export default PrintWorkOrderModal
